import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import swal from "sweetalert";
import {
    Box, Button, Card, Chip, Container, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, FormControlLabel, Grid, LinearProgress, Pagination,
    Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from "@mui/material";
import SportsMartialArtsRoundedIcon from "@mui/icons-material/SportsMartialArtsRounded";
import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded";
import { ThemeProvider } from "@mui/material/styles";
import { DashboardLayout } from "../../components/dashboard-layout";
import BlockchainAutoControl from "../../components/blockchain-auto-control";
import BlockchainBetPressure from "../../components/blockchain-bet-pressure";
import BlockchainAdminActions from "../../components/blockchain-admin-actions";
import BlockchainClassicSummary from "../../components/blockchain-classic-summary";
import BlockchainGameGuideModal from "../../components/BlockchainGameGuideModal";
import { theme } from "../../theme";

const API = process.env.REACT_APP_API_URL;
const SOCKET = String(process.env.REACT_APP_API_URL_SOCKET_TXMD560S || "https://socket.mm68bet.com").replace(/^ws/i, "http");
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("user") || ""}` } });
const money = (value) => Math.floor(Number(value || 0) / 1000).toLocaleString("vi-VN");
const unwrap = (response) => response?.data?.data;
const sideKey = (value) => {
    const key = String(value || "").toLowerCase();
    if (["dragon", "rồng", "rong"].includes(key)) return "rong";
    if (["tiger", "hổ", "ho"].includes(key)) return "ho";
    if (["tie", "hòa", "hoa"].includes(key)) return "hoa";
    return key;
};
const sideLabel = (value) => ({ rong: "Rồng", ho: "Hổ", hoa: "Hòa" }[sideKey(value)] || value || "—");
const phaseLabel = (value) => ({ BETTING: "Đang nhận cược", DEALING: "Đang mở bài", RESULT: "Công bố kết quả", WAITING: "Chờ phiên mới" }[value] || value || "Đang đồng bộ");
const SIDE_META = {
    rong: { label: "RỒNG", sub: "DRAGON", color: "#2563eb", pale: "#eff6ff", border: "#bfdbfe" },
    hoa: { label: "HÒA", sub: "TIE", color: "#059669", pale: "#ecfdf5", border: "#a7f3d0" },
    ho: { label: "HỔ", sub: "TIGER", color: "#dc2626", pale: "#fef2f2", border: "#fecaca" }
};
const panelSx = { borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px 0 rgba(0,0,0,0.04)", overflow: "hidden" };

export default function SetRongHo60s() {
    const [state, setState] = useState(null);
    const [rounds, setRounds] = useState([]);
    const [currentBets, setCurrentBets] = useState([]);
    const [history, setHistory] = useState([]);
    const [betHistory, setBetHistory] = useState([]);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [betsOpen, setBetsOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [betPage, setBetPage] = useState(1);
    const [form, setForm] = useState({ enabled: true, minBet: 10, maxBet: 100000, rong: 2, ho: 2, hoa: 9 });
    const [preset, setPreset] = useState({ roundId: "", result: "rong" });
    const [socketConnected, setSocketConnected] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [openGuideModal, setOpenGuideModal] = useState(false);

    const load = useCallback(async (showProgress = false) => {
        if (showProgress) setRefreshing(true);
        try {
            const responses = await Promise.allSettled([
                axios.get(`${API}/rongho60s/state`),
                axios.get(`${API}/rongho60s/list30bet`, auth()),
                axios.get(`${API}/rongho60s/getcurrent`, auth())
            ]);
            if (responses[0].status === "fulfilled") {
                const value = unwrap(responses[0].value);
                setState(value);
                if (value?.currentRound?._id) setPreset((old) => ({ ...old, roundId: value.currentRound._id }));
                if (value?.table) setForm({
                    enabled: Boolean(value.table.enabled), minBet: value.table.minBet,
                    maxBet: value.table.maxBet, rong: value.odds?.rong ?? 2,
                    ho: value.odds?.ho ?? 2, hoa: value.odds?.hoa ?? 9
                });
            }
            if (responses[1].status === "fulfilled") setRounds(unwrap(responses[1].value) || []);
            if (responses[2].status === "fulfilled") setCurrentBets(unwrap(responses[2].value) || []);
        } finally {
            if (showProgress) setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        load();
        const timer = window.setInterval(() => load(), 2000);
        return () => window.clearInterval(timer);
    }, [load]);

    useEffect(() => {
        const socket = io(`${SOCKET}/rongho60s`, { transports: ["websocket", "polling"], auth: { token: localStorage.getItem("user") || "" } });
        socket.on("connect", () => setSocketConnected(true));
        socket.on("disconnect", () => setSocketConnected(false));
        socket.on("table_state", (value) => {
            setState(value);
            if (!settingsOpen && value?.table) setForm({
                enabled: Boolean(value.table.enabled), minBet: value.table.minBet,
                maxBet: value.table.maxBet, rong: value.odds?.rong ?? 2,
                ho: value.odds?.ho ?? 2, hoa: value.odds?.hoa ?? 9
            });
        });
        socket.emit("sync");
        return () => socket.close();
    }, [settingsOpen]);

    const loadRoundHistory = async () => {
        const response = await axios.get(`${API}/rongho60s/getHistoryBet`);
        setHistory(unwrap(response) || []);
        setHistoryOpen(true);
    };
    const loadBetHistory = async () => {
        const response = await axios.get(`${API}/rongho60s/getAllBetByAdmin`, auth());
        setBetHistory(unwrap(response) || []);
        setBetsOpen(true);
    };
    const saveSettings = async () => {
        try {
            await axios.put(`${API}/rongho60s/table`, {
                enabled: form.enabled, minBet: Number(form.minBet), maxBet: Number(form.maxBet),
                odds: { rong: Number(form.rong), ho: Number(form.ho), hoa: Number(form.hoa) }
            }, auth());
            setSettingsOpen(false);
            await load(true);
            swal("Thành công", "Đã lưu cấu hình bàn Rồng Hổ", "success");
        } catch (error) {
            swal("Không thể cập nhật", error.response?.data?.message || "Vui lòng kiểm tra dữ liệu", "error");
        }
    };
    const savePreset = async () => {
        try {
            const response = await axios.post(`${API}/rongho60s/update`, { id_bet: preset.roundId, result: preset.result, sanh: 1 }, auth());
            await load(true);
            swal("Thành công", response?.data?.message || "Đã cập nhật kết quả cho phiên đang chạy", "success");
        } catch (error) {
            swal("Không thể cập nhật", error.response?.data?.message || "Phiên đã chuyển sang bước mở kết quả", "error");
        }
    };

    const savePresetForRow = async (targetRoundId, targetResult) => {
        try {
            const response = await axios.post(`${API}/rongho60s/update`, { id_bet: targetRoundId, result: targetResult, sanh: 1 }, auth());
            await load(true);
            swal("Thành công", response?.data?.message || "Đã cập nhật kết quả phiên Rồng Hổ", "success");
        } catch (error) {
            swal("Không thể cập nhật", error.response?.data?.message || "Phiên đã kết thúc", "error");
        }
    };
    const [tablePresets, setTablePresets] = useState({});

    const totals = useMemo(() => currentBets.reduce((out, row) => {
        const key = sideKey(row.state);
        out[key] = (out[key] || 0) + Number(row.money || 0);
        return out;
    }, { rong: 0, hoa: 0, ho: 0 }), [currentBets]);
    const grandTotal = totals.rong + totals.hoa + totals.ho;
    const adminCurrentRound = rounds.find((row) => String(row._id) === String(state?.currentRound?._id));
    const presetLocked = ["DEALING", "RESULT"].includes(state?.phase) || Number(state?.remaining || 0) <= 0;
    const roundNumber = state?.currentRound?.id_bet || "—";
    const remaining = Number(state?.remaining || 0);
    const progress = Math.max(0, Math.min(100, (remaining / Number(state?.table?.durationSeconds || 60)) * 100));

    return <ThemeProvider theme={theme}><DashboardLayout>
        <Box component="main" className="blockchain-admin-page happy-classic-game happy-classic-rongho" sx={{ flexGrow: 1, py: { xs: 2, md: 3 }, px: { xs: 1, sm: 2 }, background: "#f8fafc", minHeight: "100vh" }}>
            <Container maxWidth="xl" disableGutters>
                <div className="classic-game-guide"><button onClick={() => setOpenGuideModal(true)}>Hướng dẫn <span>?</span></button></div>
                <div className="classic-game-room-tabs classic-single-tab"><button>Rồng hổ 60 giây</button></div>
                <BlockchainAdminActions onConfig={() => setSettingsOpen(true)} onRoundHistory={loadRoundHistory} onBetHistory={loadBetHistory} />

                <BlockchainAutoControl game="rongho" />

                <Box className="classic-modern-game-title" sx={{ display: "flex", alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", md: "row" }, gap: 1.5, mb: 2 }}>
                    <Box>
                        <Typography sx={{ color: "#0f172a", fontWeight: 500, fontSize: { xs: 20, md: 22 }, lineHeight: 1.25 }}>Điều hành Rồng Hổ</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mt: 0.5 }}>
                            {socketConnected ? <WifiRoundedIcon sx={{ color: "#10b981", fontSize: 16 }} /> : <WifiOffRoundedIcon sx={{ color: "#ef4444", fontSize: 16 }} />}
                            <Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 300 }}>{socketConnected ? "Đang nhận dữ liệu thời gian thực" : "Đang kết nối lại máy chủ"}</Typography>
                        </Box>
                    </Box>
                </Box>

                <Card className="classic-rongho-round" sx={{ ...panelSx, mb: 2.5, bgcolor: "#ffffff" }}>
                    <Typography>Kỳ sổ: <Box component="span">{roundNumber === "—" ? "Đang tải..." : roundNumber}</Box></Typography>
                    <Typography>Thời gian: <Box component="span">00:{String(remaining).padStart(2, "0")}</Box></Typography>
                </Card>

                <Grid className="classic-modern-door-cards" container spacing={2} sx={{ mb: 2.5 }}>
                    {["rong", "hoa", "ho"].map((key) => {
                        const meta = SIDE_META[key];
                        const percent = grandTotal ? Math.round((totals[key] / grandTotal) * 100) : 0;
                        return <Grid item xs={12} sm={4} key={key}><Card sx={{ ...panelSx, height: "100%", position: "relative", borderColor: "#e2e8f0", bgcolor: "#ffffff" }}>
                            <Box sx={{ position: "absolute", inset: "0 auto 0 0", width: 4, bgcolor: meta.color }} />
                            <Box sx={{ p: { xs: 1.7, sm: 2 }, pl: { xs: 2.2, sm: 2.5 }, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                                <Box><Typography sx={{ color: meta.color, fontSize: 16, fontWeight: 500, letterSpacing: 0.5 }}>{meta.label}</Typography><Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 300 }}>{meta.sub} · x{state?.odds?.[key] ?? form[key]}</Typography></Box>
                                <Box sx={{ textAlign: "right" }}><Typography sx={{ color: "#0f172a", fontSize: { xs: 18, md: 20 }, fontWeight: 500 }}>{money(totals[key])}</Typography><Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 300 }}>{percent}% tổng cược</Typography></Box>
                            </Box>
                            <Box sx={{ mx: 2, mb: 1.6, height: 4, bgcolor: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}><Box sx={{ width: `${percent}%`, height: "100%", bgcolor: meta.color, borderRadius: 4, transition: "width .35s ease" }} /></Box>
                        </Card></Grid>;
                    })}
                </Grid>

                <BlockchainClassicSummary game="rongho" bets={currentBets} />

                <Grid className="classic-current-control-hidden" container spacing= {2.5} sx={{ mb: 2.5 }}>
                    <Grid item xs={12} lg={7}><CurrentBetsCard currentBets={currentBets} grandTotal={grandTotal} bettingOpen={state?.bettingOpen} /></Grid>
                    <Grid item xs={12} lg={5}>
                        <Card sx={{ ...panelSx, height: "100%", bgcolor: "#ffffff" }}><Box sx={{ p: { xs: 1.8, sm: 2.3 } }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start" }}>
                                <Box><Typography sx={{ color: "#0f172a", fontWeight: 500, fontSize: 16 }}>Kết quả phiên đang chạy</Typography><Typography sx={{ color: "#64748b", fontSize: 12, mt: 0.3, fontWeight: 300 }}>Phiên #{roundNumber} · còn {remaining} giây</Typography></Box>
                                <Chip size="small" color={presetLocked ? "warning" : "success"} label={presetLocked ? "Đã khóa" : "Có thể chỉnh"} sx={{ fontWeight: 400, borderRadius: "6px" }} />
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1 }}>
                                {["rong", "hoa", "ho"].map((key) => { const meta = SIDE_META[key]; const active = preset.result === key; return <Button key={key} disabled={presetLocked} onClick={() => setPreset((old) => ({ ...old, result: key }))} sx={{ minWidth: 0, height: 56, display: "flex", flexDirection: "column", borderRadius: "8px", textTransform: "none", border: `1px solid ${active ? meta.color : "#e2e8f0"}`, color: active ? "#fff" : meta.color, bgcolor: active ? meta.color : "#fff", "&:hover": { bgcolor: active ? meta.color : meta.pale }, "&.Mui-disabled": { opacity: 0.55 } }}><Typography sx={{ fontSize: 14, fontWeight: 500 }}>{meta.label}</Typography><Typography sx={{ fontSize: 10, opacity: 0.75 }}>{meta.sub}</Typography></Button>; })}
                            </Box>
                            <Button fullWidth variant="contained" color="warning" disabled={presetLocked || !preset.roundId} onClick={savePreset} sx={{ mt: 1.5, minHeight: 40, borderRadius: "8px", textTransform: "none", fontWeight: 400, boxShadow: "none" }}>Lưu kết quả phiên hiện tại</Button>
                            <Box sx={{ mt: 1.5, p: 1.25, borderRadius: "8px", bgcolor: adminCurrentRound?.is_admin_set ? "#fff7ed" : "#f8fafc", border: `1px solid ${adminCurrentRound?.is_admin_set ? "#fed7aa" : "#e2e8f0"}` }}><Typography sx={{ color: adminCurrentRound?.is_admin_set ? "#c2410c" : "#64748b", fontSize: 12, fontWeight: 300 }}>{adminCurrentRound?.is_admin_set ? `Đã chọn ${sideLabel(adminCurrentRound.result)} bằng CMS` : "Phiên đang sử dụng kết quả tự động"}</Typography></Box>
                        </Box></Card>
                    </Grid>
                </Grid>

                {/* Bảng Danh sách ván Rồng Hổ & Điều chỉnh kết quả cài trước */}
                <Card className="classic-result-table" sx={{ ...panelSx, mb: 3, p: 2.5, bgcolor: "#ffffff" }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: "#0f172a", fontSize: 16 }}>Danh Sách Ván Rồng Hổ & Điều Chỉnh Kết Quả Phiên</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ background: "#f8fafc" }}>
                                <TableCell sx={{ fontWeight: 500, color: "#475569" }}>Phiên</TableCell>
                                <TableCell sx={{ fontWeight: 500, color: "#475569" }}>Kết quả hiện tại</TableCell>
                                <TableCell sx={{ fontWeight: 500, textAlign: "center", color: "#475569" }}>Chọn kết quả (Rồng / Hòa / Hổ)</TableCell>
                                <TableCell sx={{ fontWeight: 500, textAlign: "center", color: "#475569" }}>Thao tác</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rounds.slice((page - 1) * 10, page * 10).map((row) => {
                                const isCurrent = String(row._id) === String(state?.currentRound?._id);
                                const isSettled = Boolean(row.settledAt);
                                const canPreset = !isSettled;
                                const currentSel = tablePresets[row._id] || sideKey(row.result) || "rong";
                                return (
                                    <TableRow key={row._id} hover sx={{ bgcolor: isCurrent ? "#eff6ff" : undefined }}>
                                        <TableCell sx={{ fontWeight: 400 }}>
                                            #{row.id_bet}{isCurrent ? " · đang chạy" : ""}
                                        </TableCell>
                                        <TableCell>
                                            <ResultBadge value={row.result} />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: "center" }}>
                                            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                                {["rong", "hoa", "ho"].map((key) => {
                                                    const meta = SIDE_META[key];
                                                    const isSel = currentSel === key;
                                                    return (
                                                        <Button
                                                            key={key}
                                                            size="small"
                                                            disabled={!canPreset}
                                                            variant={isSel ? "contained" : "outlined"}
                                                            onClick={() => setTablePresets(prev => ({ ...prev, [row._id]: key }))}
                                                            sx={{
                                                                minWidth: 70,
                                                                borderRadius: "6px",
                                                                fontWeight: 400,
                                                                textTransform: "none",
                                                                bgcolor: isSel ? meta.color : "#fff",
                                                                borderColor: meta.color,
                                                                color: isSel ? "#fff" : meta.color,
                                                                "&:hover": { bgcolor: isSel ? meta.color : meta.pale }
                                                            }}
                                                        >
                                                            {meta.label}
                                                        </Button>
                                                    );
                                                })}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: "center" }}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="warning"
                                                disabled={!canPreset}
                                                onClick={() => savePresetForRow(row._id, currentSel)}
                                                sx={{ borderRadius: "6px", textTransform: "none", fontWeight: 400, background: canPreset ? '#ef4444' : '#94a3b8', '&:hover': { background: '#dc2626' } }}
                                            >
                                                {canPreset ? "Cài trước" : "Đã khóa"}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Pagination
                            count={Math.ceil(rounds.length / 10) || 1}
                            page={page}
                            onChange={(_, p) => setPage(p)}
                            color="primary"
                            size="small"
                        />
                    </Box>
                </Card>
            </Container>


            <SettingsDialog open={settingsOpen} close={() => setSettingsOpen(false)} form={form} setForm={setForm} save={saveSettings} />
            <HistoryDialog open={historyOpen} close={() => setHistoryOpen(false)} title="Lịch sử phiên Rồng Hổ" rows={history} page={page} setPage={setPage} round />
            <HistoryDialog open={betsOpen} close={() => setBetsOpen(false)} title="Lịch sử đặt cược Rồng Hổ" rows={betHistory} page={betPage} setPage={setBetPage} />
            <BlockchainGameGuideModal open={openGuideModal} onClose={() => setOpenGuideModal(false)} />
        </Box>
    </DashboardLayout></ThemeProvider>;
}

function CurrentBetsCard({ currentBets, grandTotal, bettingOpen }) {
    return <Card sx={{ ...panelSx, height: "100%", bgcolor: "#ffffff" }}>
        <Box sx={{ px: { xs: 1.7, sm: 2.3 }, py: 1.7, borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}><Box><Typography sx={{ color: "#0f172a", fontWeight: 500, fontSize: 16 }}>Cược phiên hiện tại</Typography><Typography sx={{ color: "#64748b", fontSize: 12, mt: 0.25, fontWeight: 300 }}>{currentBets.length} lượt · {money(grandTotal)}</Typography></Box><Chip size="small" variant="outlined" color={bettingOpen ? "success" : "warning"} label={bettingOpen ? "Đang nhận" : "Đã khóa"} sx={{ fontWeight: 400, borderRadius: "6px" }} /></Box>
        <Box sx={{ overflowX: "auto", maxHeight: 330 }}><Table size="small" stickyHeader sx={{ minWidth: 590 }}>
            <TableHead><TableRow>{["Tài khoản", "Cửa cược", "Số điểm", "Trạng thái", "Thời gian"].map((head) => <TableCell key={head} sx={{ bgcolor: "#f8fafc", color: "#475569", fontSize: 12, fontWeight: 500 }}>{head}</TableCell>)}</TableRow></TableHead>
            <TableBody>{currentBets.length ? currentBets.map((row) => { const key = sideKey(row.state); const meta = SIDE_META[key] || SIDE_META.hoa; return <TableRow hover key={row._id}><TableCell sx={{ color: "#2563eb", fontWeight: 400 }}>{row.user?.username || "—"}</TableCell><TableCell><Chip size="small" label={sideLabel(key)} sx={{ bgcolor: meta.pale, color: meta.color, fontWeight: 400, border: `1px solid ${meta.border}`, borderRadius: "6px" }} /></TableCell><TableCell sx={{ color: "#d97706", fontWeight: 400 }}>{money(row.money)}</TableCell><TableCell sx={{ color: "#64748b", fontSize: 13, fontWeight: 300 }}>{row.status_bet || "Đang chờ"}</TableCell><TableCell sx={{ whiteSpace: "nowrap", color: "#64748b", fontSize: 13, fontWeight: 300 }}>{row.createdAt ? new Date(row.createdAt).toLocaleTimeString("vi-VN") : "—"}</TableCell></TableRow>; }) : <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6 }}><SportsMartialArtsRoundedIcon sx={{ color: "#cbd5e1", fontSize: 38 }} /><Typography sx={{ color: "#94a3b8", fontSize: 13, mt: 0.5, fontWeight: 300 }}>Chưa có người chơi đặt cược trong phiên này</Typography></TableCell></TableRow>}</TableBody>
        </Table></Box>
    </Card>;
}

function SettingsDialog({ open, close, form, setForm, save }) {
    return <Dialog className="blockchain-data-dialog" open={open} onClose={close} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", m: { xs: 1.5, sm: 3 } } }}>
        <DialogTitle sx={{ fontWeight: 400, pb: 1 }}>Cấu hình bàn Rồng Hổ<Typography sx={{ color: "#64748b", fontSize: 12, mt: 0.4, fontWeight: 300 }}>Giới hạn cược và tỷ lệ trả thưởng áp dụng ngay sau khi lưu</Typography></DialogTitle>
        <DialogContent dividers><Box sx={{ display: "grid", gap: 2.2, pt: 0.5 }}>
            <FormControlLabel sx={{ m: 0, p: 1.3, borderRadius: "8px", bgcolor: form.enabled ? "#ecfdf5" : "#fef2f2" }} control={<Switch checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} color="primary" />} label={<Typography fontWeight={400} fontSize={14}>{form.enabled ? "Bàn đang hoạt động" : "Bàn đang bảo trì"}</Typography>} />
            <Grid container spacing={1.5}><Grid item xs={6}><TextField fullWidth size="small" label="Cược tối thiểu" type="number" value={form.minBet} onChange={(e) => setForm({ ...form, minBet: e.target.value })} /></Grid><Grid item xs={6}><TextField fullWidth size="small" label="Cược tối đa" type="number" value={form.maxBet} onChange={(e) => setForm({ ...form, maxBet: e.target.value })} /></Grid></Grid>
            <Box><Typography sx={{ color: "#334155", fontSize: 13, fontWeight: 500, mb: 1 }}>Tỷ lệ trả thưởng</Typography><Grid container spacing={1.5}>{["rong", "hoa", "ho"].map((key) => <Grid item xs={4} key={key}><TextField fullWidth size="small" label={sideLabel(key)} type="number" inputProps={{ step: 0.01 }} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} /></Grid>)}</Grid></Box>
        </Box></DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}><Button onClick={close} sx={{ textTransform: "none" }}>Hủy</Button><Button variant="contained" onClick={save} sx={{ px: 3, borderRadius: "8px", textTransform: "none", fontWeight: 400 }}>Lưu cấu hình</Button></DialogActions>
    </Dialog>;
}

function HistoryDialog({ open, close, title, rows, page, setPage, round = false }) {
    const [searchUser, setSearchUser] = useState("");
    const [searchDate, setSearchDate] = useState("");

    const formatDateYYYYMMDD = (d) => {
        if (!d) return "";
        const dateObj = new Date(d);
        if (isNaN(dateObj.getTime())) return "";
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const formatTimeDisplay = (d) => {
        if (!d) return "—";
        const dateObj = new Date(d);
        if (isNaN(dateObj.getTime())) return "—";
        return dateObj.toLocaleString("vi-VN", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
        });
    };

    const filteredRows = (rows || []).filter(item => {
        const username = item.user?.username || item.user?.name || "";
        const matchUser = !searchUser || username.toLowerCase().includes(searchUser.toLowerCase().trim());
        let matchDate = true;
        if (searchDate) {
            const itemDate = formatDateYYYYMMDD(item.createdAt || item.created_at);
            matchDate = itemDate === searchDate;
        }
        return matchUser && matchDate;
    });

    const shown = filteredRows.slice((page - 1) * 10, page * 10);
    return <Dialog className="blockchain-data-dialog" open={open} onClose={close} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: "12px", m: { xs: 1, sm: 3 } } }}>
        <DialogTitle sx={{ fontWeight: 500, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
                <Typography variant="h6">{title}</Typography>
                <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 300 }}>{filteredRows.length} bản ghi phù hợp</Typography>
            </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                <TextField size="small" type="date" label="Khoảng thời gian" value={searchDate} onChange={(e) => { setSearchDate(e.target.value); setPage(1); }} InputLabelProps={{ shrink: true }} sx={{ width: 220 }} />
                <TextField size="small" placeholder="Tên người chơi" value={searchUser} onChange={(e) => { setSearchUser(e.target.value); setPage(1); }} sx={{ flexGrow: 1 }} />
                <Button variant="contained" onClick={() => setPage(1)} sx={{ textTransform: "none", borderRadius: "8px", px: 3, bgcolor: "#2563eb" }}>Tìm kiếm</Button>
                {(searchDate || searchUser) && <Button variant="outlined" color="secondary" onClick={() => { setSearchDate(""); setSearchUser(""); setPage(1); }} sx={{ textTransform: "none", borderRadius: "8px" }}>Xóa lọc</Button>}
            </Box>
            <Box sx={{ overflowX: "auto", minHeight: 240 }}><Table size="small" sx={{ minWidth: round ? 820 : 800 }}>
            <TableHead><TableRow>{(round ? ["Phiên", "Kết quả", "Bài Rồng", "Bài Hổ", "Tổng cược", "Tổng trả", "Thời gian"] : ["Tài khoản", "Phiên", "Cửa", "Cược", "Trả", "Trạng thái", "Thời gian"]).map((head) => <TableCell key={head} sx={{ bgcolor: "#f8fafc", color: "#475569", fontSize: 12, fontWeight: 500 }}>{head}</TableCell>)}</TableRow></TableHead>
            <TableBody>{shown.length ? shown.map((row) => round ? <TableRow hover key={row._id}><TableCell sx={{ fontWeight: 400 }}>#{row.id_bet}</TableCell><TableCell><ResultBadge value={row.result} /></TableCell><TableCell>{row.cardDragon || "—"} {row.pointsDragon !== undefined && row.pointsDragon !== null ? `(${row.pointsDragon})` : ""}</TableCell><TableCell>{row.cardTiger || "—"} {row.pointsTiger !== undefined && row.pointsTiger !== null ? `(${row.pointsTiger})` : ""}</TableCell><TableCell>{money(row.totalBet)}</TableCell><TableCell>{money(row.totalPay)}</TableCell><TableCell sx={{ color: "#64748b", whiteSpace: "nowrap", fontSize: 13 }}>{formatTimeDisplay(row.createdAt || row.created_at || row.updatedAt)}</TableCell></TableRow> : <TableRow hover key={row._id}><TableCell sx={{ fontWeight: 400, color: "#2563eb" }}>{row.user?.username || "—"}</TableCell><TableCell>#{row.id_bet?.id_bet || row.id_bet || "—"}</TableCell><TableCell><ResultBadge value={row.state} /></TableCell><TableCell sx={{ color: "#d97706" }}>{money(row.money)}</TableCell><TableCell sx={{ color: "#10b981" }}>{money(row.moneythang)}</TableCell><TableCell>{row.status_bet || "—"}</TableCell><TableCell sx={{ color: "#64748b", whiteSpace: "nowrap", fontSize: 13 }}>{formatTimeDisplay(row.createdAt || row.created_at || row.updatedAt)}</TableCell></TableRow>) : <TableRow><TableCell colSpan={7} align="center" sx={{ py: 7, color: "#94a3b8", fontWeight: 300 }}>Chưa có dữ liệu</TableCell></TableRow>}</TableBody>
        </Table></Box></DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: "space-between" }}><Pagination size="small" count={Math.max(1, Math.ceil(filteredRows.length / 10))} page={page} onChange={(_, value) => setPage(value)} color="primary" /><Button variant="outlined" onClick={close} sx={{ textTransform: "none", borderRadius: "8px" }}>Đóng</Button></DialogActions>
    </Dialog>;
}

function ResultBadge({ value }) {
    const meta = SIDE_META[sideKey(value)];
    return meta ? <Chip size="small" label={sideLabel(value)} sx={{ bgcolor: meta.pale, color: meta.color, border: `1px solid ${meta.border}`, fontWeight: 400, borderRadius: "6px" }} /> : <Chip size="small" label="Chưa có" variant="outlined" sx={{ borderRadius: "6px" }} />;
}
